﻿<template>
	<div class="comments-container">
		<div v-for="(comment, i) in comments" class="comment-container">
			<div class="read-only-existing" v-show="inReadMode(comment)">
				<div class="comment-header">
					<div class="avatar">
						<img :src="getBestAvatarUrl(comment)" width="50" height="50" />
					</div>
					<div class="info">
						<div class="name"><span>{{comment.name}}</span></div>
						<div class="time-ago" v-show="showTimeAgo(comment)"><span>{{comment.timeAgo}}</span></div>
						<div class="edit-link" v-show="ownedAndEditable(comment)" @click="toWriteMode(comment)"><span :class="clsEditLinkCta(comment)">{{getEditLinkCta(comment)}}</span></div>
						<div class="stars" v-show="isRatingComment(comment)">
							<img v-for="(starImgUrl, i) in getStarImageUrls(comment.rating)" :src="graphicPlaceholder" :data-graphic="starImgUrl" width="20" height="20" />
						</div>
					</div>
				</div>
				<div class="comment-title" v-show="comment.hasTitle">
					<span>{{comment.title}}</span>
				</div>
				<div class="comment-body" v-show="comment.hasBody">
					<span>{{comment.body}}</span>
				</div>
				<div class="editor-load-error mt-2" v-if="editorLoadError(comment)" role="alert">
					<span>Could not load the editor.</span>
					<button type="button" class="btn btn-link p-0 ms-2" @click="toWriteMode(comment)">Try again</button>
				</div>
			</div>

			<div class="read-write" v-if="ownedAndEditable(comment) && editorRequested(comment)" v-show="inWriteMode(comment)">
				<comment-editor
					:name="getAccountName(comment)"
					:avatar-url="getBestAvatarUrl(comment)"
					:index="i"
					:id="comment.id"
					:rating.number="comment.rating"
					:title="comment.title"
					:body="comment.body"
					:parent-comment-id="comment.parentCommentID"
					:top-comment-id="comment.topCommentID"
					:bundle-id="comment.bundleID"
					:is-rating="isRatingComment(comment)"
					:edit-existing="isExistingComment(comment)"
					:maxCharsTitle="maxCharsTitle"
					:maxCharsBody="maxCharsBody"
					@editor-cancelled="editorCancelled($event)"
					@comment-added="commentAdded($event)"
					@comment-edited="commentEdited($event)">
				</comment-editor>
			</div>

		</div>
	</div>
</template>




<script>
	import { graphicPlaceholder } from '@/js/shared/utils/graphics';
	import '@/scss/graphics/RatingIcons.scss';
	import { defineAsyncComponent } from 'vue';
	import { getStarImageUrls } from '@/js/shared/utils/common';
	import { isNotNullOrWhiteSpace } from '@/js/shared/utils';

	let commentEditorPromise;
	const loadCommentEditor = () => commentEditorPromise ??= import('./CommentEditor.vue').catch(error => {
		commentEditorPromise = undefined;
		throw error;
	});
	const commentEditor = defineAsyncComponent(loadCommentEditor);

	export default {
		components: {
			commentEditor
		},
		data: () => ({
			graphicPlaceholder,
			dicEditingComments : {},
			dicRequestedEditors : {},
			dicEditorErrors : {}
		}),
		props: {
			enumCommentTypes : { type: Object, required: false, default: () => ({}) },
			comments         : { type: Array, required: false, default: () => ([]) },
			idAccount        : { type: Number, required: false, default: () => 0 },
			maxCharsTitle    : { type: Number,  required: false, default: () => 0 },
			maxCharsBody     : { type: Number,  required: false, default: () => 0 },
			nrCreditsReward  : { type: Number,  required: false, default: () => 0 }
		},
		created() {
			if (this.idAccount == 0) {
				//this.dicEditingComments[0] = true;
			}
		},
		methods: {
			showTimeAgo(comment) {
				return isNotNullOrWhiteSpace(comment.timeAgo);
			},
			getBestAvatarUrl(comment) {
				var c = comment;

				return (isNotNullOrWhiteSpace(c.googleAvatarUrl))
					? c.googleAvatarUrl
					: (isNotNullOrWhiteSpace(c.facebookAvatarUrl))
					? c.facebookAvatarUrl
					: c.avatarUrl;
			},
			getAccountName(comment) {
				return comment?.name ?? '';
			},
			getTimeAgo(comment) {
				return comment?.timeAgo ?? '';
			},
			getStarImageUrls(rating) {
				return getStarImageUrls(rating);
			},
			getEditLinkCta(comment) {
				return (this.isNewComment(comment))
					? `Write a review!`
					: 'Edit'; // existing comment
			},
			isCommentType(comment, strType) {
				var ratingType = this.enumCommentTypes.byName[strType].value;
				return (comment.type == ratingType);
			},
			isRatingComment(comment) {
				return this.isCommentType(comment, 'rating');
			},
			isRegularComment(comment) {
				return this.isCommentType(comment, 'regular');
			},
			isExistingComment(comment) {
				return (comment.id != null && comment.id != 0);
			},
			isNewComment(comment) {
				return !this.isExistingComment(comment);
			},
			ownedAndEditable(comment) {
				return (comment.accountID == this.idAccount) && (comment.editable);
			},
			async toWriteMode(comment) {
				delete this.dicEditorErrors[comment.id];
				try {
					await loadCommentEditor();
					this.dicRequestedEditors[comment.id] = true;
					this.dicEditingComments[comment.id] = true;
				} catch {
					this.dicEditorErrors[comment.id] = true;
				}
			},
			editorLoadError(comment) {
				return this.dicEditorErrors[comment.id] == true;
			},
			editorRequested(comment) {
				return this.dicRequestedEditors[comment.id] == true;
			},
			toReadMode(comment) {
				delete this.dicEditingComments[comment.id];
			},
			inWriteMode(comment) {
				return (this.dicEditingComments[comment.id] == true);
			},
			inReadMode(comment) {
				return !this.inWriteMode(comment);
			},
			clsEditLinkCta(comment) {
				return {
					'cb-blue' : this.isNewComment(comment)
				};
			},
			editorCancelled(idComment) {
				var comment = { id : idComment };
				this.toReadMode(comment);
				this.$emit('editor-cancelled', idComment);
			},
			commentAdded(commentData) {
				var comment = { id : 0 };
				this.toReadMode(comment);
				this.$emit('comment-added', commentData);
			},
			commentEdited(commentData) {
				var comment = commentData.comment;
				this.toReadMode(comment);
				this.$emit('comment-edited', commentData);
			}
		},
		computed: {
		}
	};
</script>




<style scoped>
	[v-cloak] {
		display:none;
	}

	.comments-container {
		border:0px solid blue;
	}

	.comment-container {
		border:0px solid black;
	}

	.comment-container {
		border:0px solid black;
		margin-top:24px;
	}

	.comment-container:first-child {
		margin-top:0px;
	}

	.comment-header {
		display:flex;
		align-items:start;
	}

	.comment-title {
		border:0px solid green;
		margin-top:5px;
		font-weight:bold;
	}

	.comment-body {
		border:0px solid green;
		margin-top:5px;
	}

	.avatar {
	}

	.avatar img {
		width:50px;
		height:50px;
	}

	.info {
		border:0px solid red;
		margin-left:10px;
		cursor:default;
	}

	.name {
		font-weight:bold;
		display:inline-block;
	}

	.time-ago, .edit-link {
		color:gray;
		display:inline-block;
		position:relative;
	}

	.time-ago span, .edit-link span {
		margin-left:13px;
		font-size:0.95em;
	}

	.edit-link span {
		text-decoration:underline;
		font-weight:bold;
		cursor:pointer;
	}

	.edit-link span.cb-blue {
		color:#1299fd;
	}

	.time-ago span::before, .edit-link span::before {
		content: '';
		display: inline-block;
		width: 3px;
		height: 3px;
		border-radius: 5px;
		background-color: #949291;
		position: absolute;
		top:47.5%;
		left:3px;
		cursor:default;
	}

	.stars {
		display:flex;
	}

	.stars img {
		width:20px;
		height:20px;
	}
</style>
